import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';
import API from '../api/api';
import GuidedTour from './GuidedTour';
import {
    WORKSPACE_ROOT_ADMIN_VIEW,
    WORKSPACE_ROOT_CANDIDATE,
} from '../config/adminViewSession';
import './PopupManager.css';

const AUTH_PAGES = ['/login', '/signup'];

/*
 * The workspace renders no Appli chrome, and this is Appli chrome: HR-authored
 * modals, plus a tour whose copy is verbatim the vocabulary the workspace exists
 * to retire. The tour cannot fire there — its own gate below is path-scoped to
 * /work-trial/ and the legacy popup type stays bound to the legacy page — but the
 * generic modals would, so the whole component steps aside for the workspace.
 *
 * **Both of its roots.** `/admin-view/:id` is the same screen tree opened by an
 * admin, and this component is mounted above the router, so it was live there while
 * being excluded from the candidate's own copy of the identical page. Three separate
 * accidents kept it quiet — no candidate session on that origin to pass
 * `isAuthenticated()`, a `/popups/pending` read the admin-view gate refuses as
 * out-of-scope, and a tour gate gated on `/work-trial/` — and none of them is a
 * decision. An admin's frame is titled "the candidate's workspace exactly as they see
 * it", so the rule that keeps Appli chrome out of it is stated once, here, rather
 * than left resting on three coincidences.
 */
const WORKSPACE_PREFIXES = [WORKSPACE_ROOT_CANDIDATE, WORKSPACE_ROOT_ADMIN_VIEW];

/* Where Stripe sends a student back after paying or cancelling. The page is
   telling them what happened to their money; nothing opens on top of it. */
const CHECKOUT_RETURN_PREFIX = '/tests/checkout/';

const isQuietPath = (pathname) =>
    AUTH_PAGES.includes(pathname)
    || pathname.startsWith(CHECKOUT_RETURN_PREFIX)
    || WORKSPACE_PREFIXES.some((root) => pathname.startsWith(root));

const WORK_TRIAL_TOUR_STEPS = [
    {
        target: '[data-tour="wt-header"]',
        title: 'Your Work Trial',
        description: 'This is your work trial dashboard. Here you can see the position and company you\'re trialing for.',
        placement: 'bottom',
    },
    {
        target: '[data-tour="wt-tabs"]',
        title: 'Navigate Sections',
        description: 'Use these tabs to switch between Chat, Projects, and Interviews. If you\'re part of a group, Group Chat and Group Projects tabs will also appear here.',
        placement: 'bottom',
    },
    {
        target: '[data-tour="wt-chat"]',
        title: 'Chat',
        description: 'This is your conversation area. An AI assistant is here to help you, and you can type @HR in any message to send it directly to the hiring team.',
        placement: 'bottom-start',
    },
    {
        target: '[data-tour="wt-message-input"]',
        title: 'Send Messages',
        description: 'Type your messages here. Tip: type @HR in your message to send it directly to the hiring team instead of the AI.',
        placement: 'top',
    },
    {
        target: '[data-tour="wt-controls"]',
        title: 'Controls & Settings',
        description: 'Refresh your data, toggle sound and browser notifications for new messages, and access work trial settings.',
        placement: 'bottom-end',
    },
];

const PopupManager = () => {
    const location = useLocation();
    const [popups, setPopups] = useState([]);
    const [activePopupType, setActivePopupType] = useState(null); // 'modal' | 'work_trial_tour'
    const [activePopup, setActivePopup] = useState(null);
    const [pageReady, setPageReady] = useState(false);
    const fetchedRef = useRef(false);

    const isWorkTrialPage = location.pathname.startsWith('/work-trial/');
    const quiet = isQuietPath(location.pathname);

    const hasPopup = useCallback(
        (id) => popups.some((p) => p.popup_id === id),
        [popups],
    );

    const removePopup = useCallback((id) => {
        setPopups((prev) => prev.filter((p) => p.popup_id !== id));
    }, []);

    const recordInteraction = useCallback(async (popupId, action) => {
        try {
            await API.post('/popups/interaction', { popup_id: popupId, action });
        } catch {
            // non-critical
        }
    }, []);

    // Delay popup display so the page can paint first
    useEffect(() => {
        setPageReady(false);
        const timer = setTimeout(() => setPageReady(true), 800);
        return () => clearTimeout(timer);
    }, [location.pathname]);

    useEffect(() => {
        let cancelled = false;

        const fetchPopups = async () => {
            if (!isAuthenticated() || fetchedRef.current) return;

            try {
                const res = await API.get('/popups/pending');
                if (cancelled) return;

                const pending = res.data.popups ?? [];
                setPopups(pending);
                fetchedRef.current = true;
            } catch {
                // silent
            }
        };

        const timer = setTimeout(fetchPopups, 600);

        const onAuthChange = () => {
            fetchedRef.current = false;
            fetchPopups();
        };
        window.addEventListener('authStateChange', onAuthChange);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            window.removeEventListener('authStateChange', onAuthChange);
        };
    }, []);

    // Decide which popup to show based on route and pending popups
    useEffect(() => {
        if (quiet || !pageReady || popups.length === 0 || activePopupType !== null) return;

        if (isWorkTrialPage && hasPopup('candidate_work_trial_tour')) {
            setActivePopupType('work_trial_tour');
        } else {
            const modalPopup = popups.find((p) => p.popup_id !== 'candidate_work_trial_tour');
            if (modalPopup) {
                setActivePopup(modalPopup);
                setActivePopupType('modal');
            }
        }
    }, [quiet, pageReady, popups, activePopupType, hasPopup, isWorkTrialPage]);

    const handleWorkTrialTourComplete = useCallback(async () => {
        await recordInteraction('candidate_work_trial_tour', 'acted_on');
        removePopup('candidate_work_trial_tour');
        setActivePopupType(null);
    }, [recordInteraction, removePopup]);

    const handleDismiss = useCallback(async () => {
        if (!activePopup) return;

        await recordInteraction(activePopup.popup_id, 'dismissed');
        removePopup(activePopup.popup_id);
        setActivePopup(null);
        setActivePopupType(null);
    }, [activePopup, recordInteraction, removePopup]);

    const handleCta = useCallback(async () => {
        if (!activePopup) return;

        await recordInteraction(activePopup.popup_id, 'acted_on');

        if (activePopup.cta_link) {
            window.open(activePopup.cta_link, '_blank', 'noopener,noreferrer');
        }

        removePopup(activePopup.popup_id);
        setActivePopup(null);
        setActivePopupType(null);
    }, [activePopup, recordInteraction, removePopup]);

    if (quiet) return null;

    return (
        <>
            {activePopupType === 'work_trial_tour' && (
                <GuidedTour
                    steps={WORK_TRIAL_TOUR_STEPS}
                    onComplete={handleWorkTrialTourComplete}
                />
            )}

            {activePopupType === 'modal' && activePopup && (
                <div className="popup-overlay" onClick={handleDismiss}>
                    <div className="popup-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="popup-close-btn" onClick={handleDismiss}>
                            &times;
                        </button>

                        <div className="popup-body">
                            <h2>{activePopup.title}</h2>
                            <p>{activePopup.message}</p>
                        </div>

                        <div className="popup-actions">
                            {activePopup.cta_text && (
                                <button className="popup-cta-btn" onClick={handleCta}>
                                    {activePopup.cta_text}
                                </button>
                            )}
                            <button className="popup-dismiss-btn" onClick={handleDismiss}>
                                Maybe later
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PopupManager;
